export class Resizer {
  private element: HTMLElement;
  private targetPrevious: HTMLElement;
  private targetNext?: HTMLElement;
  private direction: 'horizontal' | 'vertical';
  private startX: number = 0;
  private startY: number = 0;
  private startSize: number = 0;

  constructor(
    targetPrevious: HTMLElement,
    targetNext: HTMLElement | undefined,
    direction: 'horizontal' | 'vertical' = 'horizontal',
    invertVertical: boolean = false // If true, dragging down SHRINKs the target (used when resizing bottom pane from top edge)
  ) {
    this.targetPrevious = targetPrevious;
    this.targetNext = targetNext;
    this.direction = direction;

    this.element = document.createElement('div');
    this.element.className = direction === 'horizontal' ? 'resizer-h' : 'resizer-v';

    // Style the resizer
    this.element.style.userSelect = 'none';
    this.element.style.background = 'var(--border)';
    this.element.style.zIndex = '10';
    this.element.style.transition = 'background 0.2s';
    this.element.style.flexShrink = '0'; // Important for flex

    if (direction === 'horizontal') {
      this.element.style.width = '4px';
      this.element.style.cursor = 'col-resize';
      this.element.style.height = '100%';
    } else {
      this.element.style.height = '4px';
      this.element.style.cursor = 'row-resize';
      this.element.style.width = '100%';
    }

    this.element.onmouseover = () => {
      this.element.style.background = 'var(--accent)';
    };
    this.element.onmouseout = () => {
      this.element.style.background = 'var(--border)';
    };

    this.element.onmousedown = (e) => this.onMouseDown(e, invertVertical);
  }

  private onMouseDown(e: MouseEvent, invert: boolean) {
    this.startX = e.clientX;
    this.startY = e.clientY;

    if (this.direction === 'horizontal') {
      this.startSize = this.targetPrevious.offsetWidth;
    } else {
      // Logic:
      // Case Normal: [Panel A] --- [Panel B]. Resizing A. Drag Down -> A grows.
      // Case Invert: [Panel A] --- [Panel B]. Resizing B (Editor). Drag Down -> B shrinks (because bar moves down).
      // Wait. If we have [Preview] [Handle] [Editor], and we want to change Editor height.
      // Editor is target.
      // Drag Down = Handle moves Down = Editor gets SMALLER (top edge moves down).
      // Drag Up = Handle moves Up = Editor gets BIGGER.
      // So dy needs to be subtracted if we are setting size of the Key element which is BELOW the handle.

      // Let's assume targetPrevious is the one being explicitly sized unless we pass a special flag or targetNext.
      // Actually my constructor signature is `targetPrevious`.
      // Usage for Editor: new Resizer(?, Editor, 'vert').
      // Actually, let's keep it generic.
      // If invert is true, we resize targetNext?
      // Or we assume targetPrevious is the "Top" one and we resize IT?

      // Current Main Layout:
      // Preview (flex:1)
      // Resizer
      // Editor (height: 45%)

      // If we resize Preview, Editor shrinks? BUT Editor has fixed height. Flex 1 takes remaining.
      // So we MUST resize Editor.
      // So we are resizing the element AFTER the handle.

      if (invert) {
        if (!this.targetNext) return;
        this.startSize = this.targetNext.offsetHeight;
      } else {
        this.startSize = this.targetPrevious.offsetHeight;
      }
    }

    const mouseMoveHandler = (ev: MouseEvent) => this.onMouseMove(ev, invert);
    const mouseUpHandler = () => {
      document.documentElement.removeEventListener('mousemove', mouseMoveHandler);
      document.documentElement.removeEventListener('mouseup', mouseUpHandler);
      document.body.style.cursor = '';
      this.element.style.background = 'var(--border)';
    };

    document.documentElement.addEventListener('mousemove', mouseMoveHandler, false);
    document.documentElement.addEventListener('mouseup', mouseUpHandler, false);
    document.body.style.cursor = this.direction === 'horizontal' ? 'col-resize' : 'row-resize';
    this.element.style.background = 'var(--accent)';
  }

  private onMouseMove = (e: MouseEvent, invert: boolean) => {
    e.preventDefault();
    if (this.direction === 'horizontal') {
      const dx = e.clientX - this.startX;
      this.targetPrevious.style.width = `${this.startSize + dx}px`;
    } else {
      const dy = e.clientY - this.startY;
      if (invert && this.targetNext) {
        // Drag Down (dy > 0) -> Height should decrease?
        // If handle moves down, top of Editor moves down, so Editor height decreases.
        this.targetNext.style.height = `${this.startSize - dy}px`;
      } else {
        this.targetPrevious.style.height = `${this.startSize + dy}px`;
      }
    }
  };



  get handle(): HTMLElement {
    return this.element;
  }
}
